/* RESPONSIBLE TEAM: team-help-desk-experience */

import type Store from '@ember-data/store';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import type IntlService from 'ember-intl/services/intl';
import type Session from 'embercom/services/session';
import { get } from 'embercom/lib/ajax';
export default class TeammateAiTranslationSettings extends Service {
  @tracked isInboxTranslationEnabled = true;
  @service declare appService: $TSFixMe;
  @service declare store: Store;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare session: Session;
  private readonly ADMIN_SETTINGS_FEATURE_NAME = 'realtime-translation-admin-settings';

  constructor(owner: $TSFixMe) {
    super(owner);
    taskFor(this.fetchSettings).perform();
  }

  @task({ drop: true })
  *fetchSettings(): TaskGenerator<void> {
    if (this.appService?.app?.id && this.appService.app.currentAdmin) {
      yield this.fetchOutsideInbox();
    } else {
      yield this.fetchInsideInbox();
    }
  }

  private async fetchOutsideInbox(): Promise<void> {
    try {
      if (this.appService.app.canUseFeature(this.ADMIN_SETTINGS_FEATURE_NAME)) {
        let adminId = this.appService.app.currentAdmin.id;
        let appSettings =
          this.store.peekRecord('admin-teammate-app-settings', adminId) ||
          (await this.store.findRecord('admin-teammate-app-settings', adminId));
        this.isInboxTranslationEnabled = appSettings.get('translation_enabled');
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(
          'new-settings.personal.preferences.ai-translation-settings-section.loading-error',
        ),
      );
    }
  }

  private async fetchInsideInbox(): Promise<void> {
    try {
      if (
        this.session.isWorkspaceLoaded &&
        this.session.workspace.isFeatureEnabled(this.ADMIN_SETTINGS_FEATURE_NAME)
      ) {
        let teammate = this.session.teammate;
        let response = await get(`/ember/admins/teammate_app_settings/${teammate.id}`, {
          app_id: this.session.workspace.id,
        });
        this.isInboxTranslationEnabled = response.translation_enabled;
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(
          'new-settings.personal.preferences.ai-translation-settings-section.loading-error',
        ),
      );
    }
  }

  @task({ drop: true })
  *toggleInboxTranslationConfiguration(): TaskGenerator<void> {
    try {
      let adminId = this.appService.app.currentAdmin.id;
      let appSettings =
        this.store.peekRecord('admin-teammate-app-settings', adminId) ||
        (yield this.store.findRecord('admin-teammate-app-settings', adminId));
      appSettings.set('translation_enabled', !this.isInboxTranslationEnabled);
      yield appSettings.save();
      this.isInboxTranslationEnabled = !this.isInboxTranslationEnabled;
      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'new-settings.personal.preferences.ai-translation-settings-section.toggle-success',
        ),
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(
          'new-settings.personal.preferences.ai-translation-settings-section.toggle-error',
        ),
      );
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'teammate-ai-translation-settings': TeammateAiTranslationSettings;
  }
}
